import { observable, computed } from 'mobx';
import { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import {
    TPaymentMethod,
    IChargeBlockResponse,
    IChargeVariableValue,
    AutoPayStatus,
    Currency
} from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { Variable } from '~/models/variable.model';
import type { FlowRevision } from '~/models/flow-revision.model';
import { PaymentStatus, TGratuityTypes } from '~/types/model.types';
import { IPaidPaymentInfo, TChargeStatus } from '~/types/pay.types';
import { getPaymentDateDisplayValue } from '~/components/common/InvoiceBlock/invoice.utils';
import { daysDifference } from '~/utils/date.utils';

export class CXChargeBlock extends CXBaseBlock {
    type: 'charge_block';
    @observable private _payment_method_types: TPaymentMethod[];
    @observable allow_autopay: boolean;
    @observable allow_gratuity: boolean;
    @observable gratuity_type: TGratuityTypes;

    constructor(
        block: IChargeBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    set payment_method_types(value: TPaymentMethod[]) {
        this._payment_method_types = value;
    }

    get payment_method_types(): TPaymentMethod[] {
        if (this.flow?.isLeadForm) {
            return ['credit_card'];
        }
        return (
            this._payment_method_types ||
            (this.flow?.currency === Currency.USD
                ? ['bank_account', 'credit_card']
                : ['credit_card'])
        );
    }

    isOnlyAllowedPaymentMethod(pm: TPaymentMethod) {
        return (
            this.payment_method_types.length === 1 &&
            this.payment_method_types[0] === pm
        );
    }

    get connectedVariable(): Variable<IChargeVariableValue> {
        return super.connectedVariable;
    }

    get noItemsOnInvoice() {
        return !this.flow?.invoiceContainerBlock?.allMainServiceItemBlocks
            ?.length;
    }

    getChargeProps(paidPaymentInfo: IPaidPaymentInfo, payInAdvanceMode) {
        const {
            paymentsBlocksCount,
            lastPendingPayment,
            currency,
            expired,
            upcomingPayment
        } = this.flow;

        const isRecurring =
            this.flow?.invoiceContainerBlock?.isRecurringInvoice;

        const isRecurringDisabled = this.flow.revision.isRecurringDisabled;

        const lastPaidPaymentBlock = this.flow?.lastPaidPayment;

        const lastPaidPaymentVariable =
            lastPaidPaymentBlock?.connectedVariable?.value;

        const hasPendingPayment = isRecurring
            ? lastPaidPaymentBlock?.isPending
            : !!lastPendingPayment;

        const overduePaymentBlocks = this.flow?.paymentsBlocks?.filter(
            payment => payment.paymentStatus === PaymentStatus.Overdue
        );

        const noItemsOnInvoice = this.noItemsOnInvoice;

        const canPayInAdvance =
            !expired &&
            !isRecurringDisabled &&
            !isRecurring &&
            !!upcomingPayment &&
            !hasPendingPayment &&
            (upcomingPayment?.isMilestone
                ? !!upcomingPayment?.milestoneCompletedOn
                : true);

        const chargeStatus = this.getChargeStatus(
            expired,
            isRecurringDisabled,
            hasPendingPayment,
            paidPaymentInfo,
            lastPaidPaymentVariable,
            payInAdvanceMode,
            noItemsOnInvoice,
            !!overduePaymentBlocks?.length,
            upcomingPayment,
            upcomingPayment?.isMilestone
        );

        const dateToShow = getPaymentDateDisplayValue({
            date: upcomingPayment?.paymentDueDate,
            paymentVariable: upcomingPayment?.connectedVariable?.value,
            paymentContent: upcomingPayment?.payment_content,
            isRecurring
        });
        if (chargeStatus === 'lastReceipt') {
            return {
                totalAmount:
                    paidPaymentInfo?.amount ??
                    lastPaidPaymentBlock?.totalAmount,
                chargeStatus,
                computedChargeId:
                    paidPaymentInfo?.charge_id ??
                    lastPaidPaymentVariable?.charge_id,
                receiptAmount:
                    paidPaymentInfo?.amount ??
                    lastPaidPaymentBlock?.paymentAmount,
                receiptGratuity:
                    paidPaymentInfo?.gratuity ?? lastPaidPaymentBlock?.tipPaid,
                receiptChargeDescriptor:
                    paidPaymentInfo?.charge_statement_descriptor ||
                    lastPaidPaymentVariable?.charge_statement_descriptor,
                receiptPayerEmail:
                    paidPaymentInfo?.payer_email ||
                    lastPaidPaymentVariable?.payment_payer_email,
                nextPaymentDueDate: dateToShow,
                receiptPayerName:
                    paidPaymentInfo?.payer_name ||
                    lastPaidPaymentVariable?.payment_payer_name,
                currentPaymentNumber: paidPaymentInfo?.payment_number, // already transformed to 1
                totalPaymentsNumber: paymentsBlocksCount,
                chargeDate:
                    paidPaymentInfo?.charge_date ||
                    lastPaidPaymentVariable?.charge_date,
                isManualPaid:
                    lastPaidPaymentVariable?.is_paid &&
                    lastPaidPaymentVariable?.is_manual,
                receiptPaymentMethod: {
                    last4:
                        paidPaymentInfo?.payment_method_last4 ??
                        lastPaidPaymentVariable?.payment_method_last4,
                    typeName:
                        paidPaymentInfo?.payment_method_type_name ??
                        lastPaidPaymentVariable?.payment_method_type_name,
                    methodType:
                        paidPaymentInfo?.payment_method_type ??
                        lastPaidPaymentVariable?.payment_method_type
                },
                flowExpired: expired,
                canPayInAdvance,
                currency,
                allPaymentsPaid: !upcomingPayment || expired,
                isRecurringInvoice: isRecurring,
                isLastPaymentPending: hasPendingPayment,
                paymentBlockId: lastPaidPaymentBlock?.block_id,
                lateFee: lastPaidPaymentBlock?.lateFee
            };
        }

        if (chargeStatus === 'status') {
            return {
                chargeStatus,
                computedChargeId: upcomingPayment?.paymentChargeId,
                allPaymentsPaid:
                    !upcomingPayment || expired || isRecurringDisabled,
                nextPaymentDueDate: upcomingPayment?.paymentDueDate,
                nextPaymentAmount: upcomingPayment?.paymentAmount,
                milestonePayment: upcomingPayment?.isMilestone,
                milestonePaymentOpen: !!upcomingPayment?.milestoneCompletedOn,
                overdueCount: overduePaymentBlocks?.length,
                canPayInAdvance,
                isLastPaymentPending: hasPendingPayment,
                isRecurringInvoice: isRecurring,
                flowExpired: expired,
                isRecurringDisabled,
                noItemsOnInvoice,
                upcomingPaymentDateToShow: dateToShow,
                upcomingPaymentNumber: upcomingPayment?.paymentIndex,
                totalPaymentsNumber: paymentsBlocksCount,
                paymentBlockId: upcomingPayment?.block_id,
                lateFee: upcomingPayment?.lateFee
            };
        }

        return {
            chargeStatus,
            payInAdvanceMode: payInAdvanceMode,
            computedChargeId: upcomingPayment?.paymentChargeId,
            overdueCount: overduePaymentBlocks?.length,
            chargePaymentDueDate: dateToShow,
            noItemsOnInvoice,
            isRecurringInvoice: isRecurring,
            paymentAmount: upcomingPayment?.paymentAmount,
            paymentId: upcomingPayment?.paymentId,
            paymentIndex: upcomingPayment?.paymentIndex,
            paymentBlockId: upcomingPayment?.block_id,
            currentPaymentBlock: upcomingPayment,
            lateFee: upcomingPayment?.lateFee
        };
    }

    getChargeStatus(
        expired: boolean,
        isRecurringDisabled: boolean,
        hasPendingPayment,
        paidPaymentInfo: IPaidPaymentInfo,
        lastPaidPayment,
        payInAdvanceMode,
        noItemsOnInvoice: boolean,
        hasOverduePaymentBlocks: boolean,
        upcomingPayment,
        upcomingPaymentIsMilestone
    ): TChargeStatus {
        const justPaid = !!paidPaymentInfo?.charge_id;
        const upcomingDueDateLessThen4Days =
            daysDifference(upcomingPayment?.paymentDueDate, new Date()) < 4;
        const isUpcomingMSNotCompleted =
            upcomingPayment &&
            upcomingPaymentIsMilestone &&
            !upcomingPayment?.milestoneCompletedOn;

        if (justPaid && !payInAdvanceMode) {
            return 'lastReceipt';
        }

        if (
            expired ||
            isRecurringDisabled ||
            hasPendingPayment ||
            isUpcomingMSNotCompleted
        ) {
            return 'status';
        }
        if (
            payInAdvanceMode ||
            noItemsOnInvoice ||
            hasOverduePaymentBlocks ||
            upcomingDueDateLessThen4Days ||
            (!lastPaidPayment && !!upcomingPayment)
        ) {
            return 'charge';
        }

        return 'status';
    }

    getGenericChargeStatus(): string {
        const paidPaymentInfo = this.rootStore.flowViewUiStore.paidPaymentInfo;
        const payInAdvanceMode =
            this.rootStore.flowViewUiStore.payInAdvanceMode;

        const { chargeStatus } = this.getChargeProps(
            paidPaymentInfo,
            payInAdvanceMode
        );

        return chargeStatus;
    }

    @computed
    get isAutoPayActive() {
        return (
            this.connectedVariable?.value?.auto_payment?.status ===
            AutoPayStatus.Active
        );
    }

    @computed
    get autoPayStatus() {
        return this.connectedVariable?.value?.auto_payment?.status;
    }
}
